import React, { useCallback } from 'react';
import { useStore, getSmoothStepPath, EdgeProps, Node } from 'reactflow';
import { getEdgeParams } from '../../utils/floatingEdge';

interface ElkPoint {
  x: number;
  y: number;
}

/**
 * Bouwt een orthogonal SVG-pad door de bend-points heen die ELK heeft
 * berekend. De source/target zijn de echte node-anchor punten van React Flow,
 * zodat de aansluiting op de node mooi blijft.
 */
function buildPathThroughBendPoints(
  sx: number, sy: number,
  tx: number, ty: number,
  bendPoints: ElkPoint[]
): string {
  if (bendPoints.length === 0) {
    return `M ${sx},${sy} L ${tx},${ty}`;
  }
  const parts = [`M ${sx},${sy}`];
  bendPoints.forEach((p) => parts.push(`L ${p.x},${p.y}`));
  parts.push(`L ${tx},${ty}`);
  return parts.join(' ');
}

const FloatingEdge: React.FC<EdgeProps> = ({
  id,
  source,
  target,
  markerEnd,
  style,
  label,
  labelStyle,
  labelBgStyle,
  labelBgPadding,
  labelBgBorderRadius,
  animated,
  data,
}) => {
  const sourceNode = useStore(
    useCallback((s) => s.nodeInternals.get(source), [source])
  ) as Node | undefined;
  const targetNode = useStore(
    useCallback((s) => s.nodeInternals.get(target), [target])
  ) as Node | undefined;

  if (!sourceNode || !targetNode) {
    return null;
  }

  // Default anchor uit React Flow's eigen berekening (fallback)
  const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(
    sourceNode,
    targetNode
  );

  // Defensief: data kan undefined zijn als layoutGraph niet is gerund.
  const rawBendPoints = data && (data as any).bendPoints;
  const bendPoints: ElkPoint[] = Array.isArray(rawBendPoints)
    ? (rawBendPoints as ElkPoint[]).filter(
        (p) => p && typeof p.x === 'number' && typeof p.y === 'number'
      )
    : [];
  const elkStart = data && ((data as any).elkStart as ElkPoint | undefined);
  const elkEnd = data && ((data as any).elkEnd as ElkPoint | undefined);

  let edgePath: string;
  let labelX: number;
  let labelY: number;

  if (bendPoints.length > 0) {
    // Gebruik ELK's exacte start/end punten als beschikbaar — die liggen
    // op de node-rand precies waar de edge ELK's routing volgt. Dit
    // voorkomt een schuine "join-segment" tussen ons anchor en het eerste
    // bend-point dat door andere nodes zou kunnen lopen.
    const startX = elkStart && typeof elkStart.x === 'number' ? elkStart.x : sx;
    const startY = elkStart && typeof elkStart.y === 'number' ? elkStart.y : sy;
    const endX = elkEnd && typeof elkEnd.x === 'number' ? elkEnd.x : tx;
    const endY = elkEnd && typeof elkEnd.y === 'number' ? elkEnd.y : ty;

    edgePath = buildPathThroughBendPoints(startX, startY, endX, endY, bendPoints);
    // Label op het middelste bend-point voor leesbaarheid
    const midIdx = Math.floor(bendPoints.length / 2);
    const midPoint = bendPoints[midIdx];
    labelX = midPoint.x;
    labelY = midPoint.y;
  } else {
    // Geen bend-points → smoothstep fallback (oorspronkelijke gedrag)
    const result = getSmoothStepPath({
      sourceX: sx,
      sourceY: sy,
      sourcePosition: sourcePos,
      targetX: tx,
      targetY: ty,
      targetPosition: targetPos,
    });
    edgePath = result[0];
    labelX = result[1];
    labelY = result[2];
  }

  return (
    <>
      <path
        id={id}
        className={`react-flow__edge-path ${animated ? 'animated' : ''}`}
        d={edgePath}
        markerEnd={markerEnd}
        // fill="none" expliciet ipv via CSS-class — html-to-image PNG/SVG
        // export pakt CSS-classes niet altijd mee, waardoor SVG paths
        // anders als zwarte driehoeken worden gerenderd.
        fill="none"
        style={style}
      />
      {label && (
        <g transform={`translate(${labelX}, ${labelY})`}>
          {labelBgStyle && (
            <rect
              x={-(String(label).length * 4 + (labelBgPadding?.[0] ?? 4))}
              y={-10 - (labelBgPadding?.[1] ?? 4)}
              width={String(label).length * 8 + (labelBgPadding?.[0] ?? 4) * 2}
              height={20 + (labelBgPadding?.[1] ?? 4) * 2}
              rx={labelBgBorderRadius ?? 3}
              style={labelBgStyle as React.CSSProperties}
            />
          )}
          <text
            style={labelStyle as React.CSSProperties}
            textAnchor="middle"
            dominantBaseline="central"
            className="react-flow__edge-text"
          >
            {label}
          </text>
        </g>
      )}
    </>
  );
};

export default FloatingEdge;
